import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useLanding } from "@/context/LandingContext";
import { InteractiveSection, MotionBg } from "./MotionBg";
import { Reveal } from "./Reveal";

const listVariants = { hidden: {}, show: { transition: { staggerChildren: 0.09, delayChildren: 0.05 } } };
const OUTCOME_INITIAL = { opacity: 0, y: 8 };
const OUTCOME_ANIMATE = { opacity: 1, y: 0 };
const OUTCOME_TRANSITION = { delay: 0.5, duration: 0.4 };
const PANEL_INITIAL = { opacity: 0, y: 12 };
const PANEL_ANIMATE = { opacity: 1, y: 0 };
const PANEL_EXIT = { opacity: 0, y: -8 };
const PANEL_TRANSITION = { duration: 0.3 };
const itemVariants = { hidden: { opacity: 0, x: -10 }, show: { opacity: 1, x: 0, transition: { duration: 0.38, ease: [0.2, 0.8, 0.2, 1] } } };

function Column({ kind, heading, badge, steps, outcome, testId }) {
  return (
    <div className={`scenario-col ${kind}`} data-testid={testId}>
      <div className="scenario-col-head">
        <h3>{heading}</h3>
        <span className="scenario-badge">{badge}</span>
      </div>
      <motion.ol className="scenario-steps" variants={listVariants} initial="hidden" animate="show">
        {steps.map((step, index) => (
          <motion.li className="scenario-step" key={step.title} variants={itemVariants}>
            <span className="scenario-step-no">0{index + 1}</span>
            <div>
              <strong>{step.title}</strong>
              <span>{step.copy}</span>
            </div>
          </motion.li>
        ))}
      </motion.ol>
      <motion.p className="scenario-outcome" initial={OUTCOME_INITIAL} animate={OUTCOME_ANIMATE} transition={OUTCOME_TRANSITION}>
        {outcome}
      </motion.p>
    </div>
  );
}

// "What happens next?" simulator: pick a real-life event and compare the family's path with and without an LPA.
export function Scenario() {
  const { t, setQuestionId, setRegisterOpen } = useLanding();
  const s = t.scenario;
  const [active, setActive] = useState(0);
  if (!s) return null;
  const item = s.items[active] || s.items[0];

  const onReserve = () => {
    setQuestionId(item.question || "without");
    setRegisterOpen(true);
  };

  return (
    <InteractiveSection className="section section-dark scenario-section" id="scenario" data-testid="scenario-section">
      <MotionBg variant="network" />
      <div className="container">
        <Reveal className="scenario-head">
          <p className="eyebrow">{s.eyebrow}</p>
          <h2>{s.title}</h2>
          <p className="lead">{s.lead}</p>
        </Reveal>
        <Reveal className="scenario-picker" role="tablist" aria-label={s.pickerLabel} delay={0.05}>
          {s.items.map((option, index) => (
            <button
              key={option.label}
              type="button"
              role="tab"
              aria-selected={active === index}
              className={`scenario-chip ${active === index ? "active" : ""}`}
              onClick={() => setActive(index)}
              data-testid={`scenario-chip-${index}`}
            >
              <span className="scenario-chip-icon" aria-hidden="true">{option.icon}</span>
              {option.label}
            </button>
          ))}
        </Reveal>
        <AnimatePresence mode="wait">
          <motion.div key={active} initial={PANEL_INITIAL} animate={PANEL_ANIMATE} exit={PANEL_EXIT} transition={PANEL_TRANSITION}>
            <p className="scenario-context" data-testid="scenario-context">
              <span aria-hidden="true">→</span>
              <span><strong>{item.contextLabel}</strong> {item.context}</span>
            </p>
            <div className="scenario-compare">
              <Column kind="without" heading={s.withoutHeading} badge={s.withoutBadge} steps={item.without} outcome={item.withoutOutcome} testId="scenario-without" />
              <Column kind="with" heading={s.withHeading} badge={s.withBadge} steps={item.with} outcome={item.withOutcome} testId="scenario-with" />
            </div>
          </motion.div>
        </AnimatePresence>
        <Reveal className="scenario-footer" delay={0.1}>
          <p>
            <strong>{s.footerStrong}</strong> {s.footerCopy}
          </p>
          <button type="button" className="btn btn-gold" onClick={onReserve} data-testid="scenario-cta">
            {s.button} <span className="arrow">→</span>
          </button>
        </Reveal>
        <p className="scenario-note">{s.note}</p>
      </div>
    </InteractiveSection>
  );
}
